import { translator } from '@/i18n'
import { projectsMessages } from '@/i18n/messages/projects'

/**
 * Project lifecycle statuses, ported verbatim from AU's project edit form
 * (`Pages/Shared/ProjectsNew/_AddEditContract.cshtml`). The integer codes are AU's; the labels
 * are AU's German ones, translated per language in `i18n/messages/projects.ts` (status0 …
 * status12) and looked up at render time. Colour groups the ladder into phases so the list
 * reads at a glance — pre-contract (amber) → contracted (blue) → permitting (violet) → civil
 * works (green).
 *
 * 999 and any unknown code fall back to a neutral badge showing the raw number rather than
 * inventing a label.
 */
export interface ProjectStatusDef {
  code: number
  /** The translated label, in the current language. */
  label: string
  color: string
}

const STATUS_COLORS: readonly { code: number; color: string }[] = [
  { code: 0, color: '#6b7280' },
  { code: 1, color: '#d97706' },
  { code: 2, color: '#d97706' },
  { code: 3, color: '#d97706' },
  { code: 4, color: '#2563eb' },
  { code: 5, color: '#2563eb' },
  { code: 6, color: '#7c3aed' },
  { code: 7, color: '#7c3aed' },
  { code: 8, color: '#7c3aed' },
  { code: 9, color: '#3ca11b' },
  { code: 10, color: '#0d9488' },
  { code: 11, color: '#16a34a' },
  { code: 12, color: '#16a34a' },
]

type StatusKey = `status${0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12}`

const BY_CODE = new Map(STATUS_COLORS.map((s) => [s.code, s]))

/** All known statuses, labelled in the current language. Call at render time. */
export function projectStatuses(): ProjectStatusDef[] {
  const t = translator(projectsMessages)
  return STATUS_COLORS.map((s) => ({ ...s, label: t(`status${s.code}` as StatusKey) }))
}

/** One status, labelled in the current language. Call at render time. */
export function projectStatus(code: number | null | undefined): ProjectStatusDef {
  const t = translator(projectsMessages)
  if (code == null) return { code: -1, label: t('statusNone'), color: '#9ca3af' }
  const known = BY_CODE.get(code)
  if (known) return { ...known, label: t(`status${code}` as StatusKey) }
  return { code, label: t('statusUnknown', { code: String(code) }), color: '#9ca3af' }
}
